"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrderCacheService = void 0;
const lodash_1 = require("lodash");
const mobx_1 = require("mobx");
const Order_1 = require("../models/Order");
const types_1 = require("../types");
/**
 * An opinionated service to maintain a performant caching layer of Order data.
 * This is implemented by `GmOrderbookService`.
 */
class OrderCacheService {
    constructor() {
        this.endTriggered = false;
        this.observableData = {
            buyOrdersCache: new Map(),
            sellOrdersCache: new Map(),
        };
        this.orderChanges = (0, mobx_1.observable)([]);
        this.observableData = (0, mobx_1.makeAutoObservable)(this.observableData);
        (0, mobx_1.makeObservable)(this, {
            buyOrdersCache: mobx_1.computed,
            sellOrdersCache: mobx_1.computed,
            allOrdersCache: mobx_1.computed,
        });
    }
    get buyOrdersCache() {
        return this.observableData.buyOrdersCache;
    }
    get sellOrdersCache() {
        return this.observableData.sellOrdersCache;
    }
    get allOrdersCache() {
        const map = new Map();
        this.buyOrdersCache.forEach((ordersForMint) => {
            ordersForMint.forEach((order, key) => {
                map.set(key, order);
            });
        });
        this.sellOrdersCache.forEach((ordersForMint) => {
            ordersForMint.forEach((order, key) => {
                map.set(key, order);
            });
        });
        return map;
    }
    get mints() {
        return (0, lodash_1.union)(Array.from(this.buyOrdersCache.keys()), Array.from(this.sellOrdersCache.keys()));
    }
    get orders() {
        return this.allOrdersCache;
    }
    get buyOrders() {
        const map = new Map();
        this.buyOrdersCache.forEach((ordersForMint) => {
            ordersForMint.forEach((order) => {
                map.set(order.id, order);
            });
        });
        return map;
    }
    get sellOrders() {
        const map = new Map();
        this.sellOrdersCache.forEach((ordersForMint) => {
            ordersForMint.forEach((order) => {
                map.set(order.id, order);
            });
        });
        return map;
    }
    clearOrders() {
        const { allOrdersCache } = this;
        allOrdersCache.forEach((order) => {
            (0, mobx_1.runInAction)(() => {
                this.orderChanges.push({
                    eventType: types_1.GmEventType.orderRemoved,
                    order,
                });
            });
        });
        this.buyOrdersCache.clear();
        this.sellOrdersCache.clear();
    }
    getAllOrdersCache() {
        return this.allOrdersCache;
    }
    getOrderCacheByType(type) {
        return type === Order_1.OrderSide.Buy
            ? this.getBuyOrderCache()
            : this.getSellOrderCache();
    }
    getBuyOrderCache() {
        return new Map(this.buyOrdersCache);
    }
    getSellOrderCache() {
        return new Map(this.sellOrdersCache);
    }
    getOrderById(id) {
        return this.allOrdersCache.get(id);
    }
    addOrder(order) {
        const ordersForType = this.getOrdersForType(order.orderType);
        if (!ordersForType.get(order.orderMint)) {
            ordersForType.set(order.orderMint, new Map());
        }
        const ordersForMint = ordersForType.get(order.orderMint);
        const existingOrder = ordersForMint.get(order.id);
        if (existingOrder && existingOrder.slotContext > order.slotContext) {
            return existingOrder;
        }
        ordersForMint.set(order.id, order);
        (0, mobx_1.runInAction)(() => {
            this.orderChanges.push({
                eventType: types_1.GmEventType.orderAdded,
                order,
            });
        });
        return order;
    }
    updateOrder(order) {
        const ordersForType = this.getOrdersForType(order.orderType);
        const ordersForMint = ordersForType.get(order.orderMint);
        const existingOrder = ordersForMint.get(order.id);
        if (!existingOrder)
            return;
        if (existingOrder.orderQtyRemaining !== order.orderQtyRemaining &&
            order.slotContext > existingOrder.slotContext) {
            const orderClone = (0, lodash_1.clone)(existingOrder);
            orderClone.orderQtyRemaining = order.orderQtyRemaining;
            ordersForMint.set(order.id, orderClone);
            (0, mobx_1.runInAction)(() => {
                this.orderChanges.push({
                    eventType: types_1.GmEventType.orderModified,
                    order,
                });
            });
        }
        return order;
    }
    cancelOrder(order) {
        const ordersForType = this.getOrdersForType(order.orderType);
        const ordersForMint = ordersForType.get(order.orderMint);
        const removed = ordersForMint.delete(order.id);
        if (removed) {
            (0, mobx_1.runInAction)(() => {
                this.orderChanges.push({
                    eventType: types_1.GmEventType.orderRemoved,
                    order,
                });
            });
            return true;
        }
        return false;
    }
    getOrdersForType(orderType) {
        if (orderType === Order_1.OrderSide.Buy) {
            return this.buyOrdersCache;
        }
        return this.sellOrdersCache;
    }
}
exports.OrderCacheService = OrderCacheService;
//# sourceMappingURL=OrderCacheService.js.map